import { HttpClientModule, HttpClientXsrfModule } from '@angular/common/http';
import { NgModule, inject, provideAppInitializer } from '@angular/core';
import { MatDialogModule } from '@angular/material/dialog';
import { MatSnackBarModule } from '@angular/material/snack-bar';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { LcsDateAdapter } from '@lcs/shared/core';
import { LCS_DATE_RANGE_PICKER_OPTIONS } from '@lcs/shared/datepicker';
import { ReportEffects } from '@ndt/reports/shared/state/report.effects';
import { EffectsModule } from '@ngrx/effects';
import { StoreRouterConnectingModule } from '@ngrx/router-store';
import { RootStoreConfig, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import { NgxChartsModule } from '@swimlane/ngx-charts';
import { environment } from 'src/environments/environment';
import { EnvironmentService } from 'src/environments/environment.service';
import { NdtEnvironmentService } from 'src/environments/ndt-environment.service';
import { BulkConfigureExtensionsDialogModule } from '../../src/app/common-components/bulk-configure-extensions-dialog/bulk-configure-extensions-dialog.module';
import { AppConfigService } from './app-config/app-config-service';
import { AppRoutingModule } from './app-routing.module';
import { AppStartEffects } from './app-start/app-start.effects';
import { AppComponent } from './app.component';
import { AppEffects } from './app.effects';
import { AppState, reducers } from './app.reducer';
import { AuthenticationModule } from './authentication/authentication.module';
import { AuthenticationEffects } from './authentication/state/authenication.effects';
import { AutoAttendantKeyListModule } from './common-components/auto-attendant-key-list/auto-attendant-key-list.module';
import { AvatarModule } from './common-components/avatar/avatar.module';
import { ConfirmationDialogModule } from './common-components/confirmation-dialog/confirmation-dialog.module';
import { AccountOptionEffects } from './common-components/entity-select/account/state/account-option.effects';
import { DepartmentOptionEffects } from './common-components/entity-select/department/state/department-option.effects';
import { DeviceTypeOptionEffects } from './common-components/entity-select/device-type/state/device-type-option.effects';
import { ExtensionGroupOptionEffects } from './common-components/entity-select/extension-group/state/extension-group-option.effects';
import { ExtensionTypeOptionEffects } from './common-components/entity-select/extension-type/state/extension-type-option.effects';
import { ExtensionOptionEffects } from './common-components/entity-select/extension/state/extension-option.effects';
import { LocationOptionEffects } from './common-components/entity-select/location/state/location-option.effects';
import { DeviceOptionEffects } from './common-components/entity-select/phone/state/device-option.effects';
import { PhoneNumberOptionEffects } from './common-components/entity-select/phonenumber/state/phone-number-option.effects';
import { PhoneOptionEffects } from './common-components/entity-select/phones/state/phone-option.effects';
import { PromptOptionEffects } from './common-components/entity-select/prompt/state/prompt-option.effects';
import { RoleOptionEffects } from './common-components/entity-select/role/state/role-option.effects';
import { ScheduleOptionEffects } from './common-components/entity-select/schedule/state/schedule-option.effects';
import { ServerOptionEffects } from './common-components/entity-select/server/state/server-option.effects';
import { UserOptionEffects } from './common-components/entity-select/user/state/user-option.effects';
import { ErrorMessageModule } from './common-components/errors/error-message.module';
import { GlobalSearchModule } from './common-components/global-search/global-search.module';
import { NdtLogoModule } from './common-components/ndt-logo/ndt-logo.module';
import { PrivilegeListModule } from './common-components/privileges-list/privileges-list.module';
import { dateRangePickerOptionsForCallHistory } from './date-picker/date-picker-options';
import { AccessDeniedModule } from './pages/access-denied/access-denied.module';
import { AccountDetailModule } from './pages/account-details/account-detail.module';
import { AccountRegisterModule } from './pages/account-register/account-register.module';
import { AccountVerificationModule } from './pages/account-verification/account-verification.module';
import { ActiveCallsRegisterModule } from './pages/active-calls/active-calls-register.module';
import { AddAccountModule } from './pages/add-account/add-account.module';
import { AddBlockedPhoneNumberModule } from './pages/add-blocked-phone-number/add-blocked-phone-number.module';
import { AddDepartmentModule } from './pages/add-department/add-department.module';
import { AddDeviceModule } from './pages/add-devices/add-device.module';
import { AddExtensionGroupModule } from './pages/add-extension-group/add-extension-group.module';
import { AddExtensionModule } from './pages/add-extension/add-extension.module';
import { AddLocationModule } from './pages/add-location/add-location.module';
import { AddPhoneNumberModule } from './pages/add-phone-number/add-phone-number.module';
import { AddHardphoneModule } from './pages/add-phone-to-device/add-hardphone.module';
import { AddPhoneModule } from './pages/add-phone/add-phone.module';
import { AddPromptModule } from './pages/add-prompt/add-prompt.module';
import { AddProviderCallCostModule } from './pages/add-provider-call-costs/add-provider-call-costs.module';
import { AddRoleModule } from './pages/add-role/add-role.module';
import { AddScheduleModule } from './pages/add-schedule/add-schedule.module';
import { AddUserModule } from './pages/add-user/add-user.module';
import { AllowedIpRegisterModule } from './pages/allowed-ips-register/allowed-ip-register.module';
import { BannedProvisioningIpRegisterModule } from './pages/banned-provisioning-ip-register/banned-provisioning-ip-register.module';
import { BlockedPhoneNumberRegisterModule } from './pages/blocked-phone-number-register-component/blocked-phone-number-register.module';
import { CallCostDetailModule } from './pages/call-cost-details/call-cost-detail.module';
import { CallCostRegisterModule } from './pages/call-cost-register/call-cost-register.module';
import { CallLogsRegisterModule } from './pages/call-logs-register/call-logs-register.module';
import { CallParkingLotRegisterModule } from './pages/call-parking-register/call-logs-register.module';
import { DashboardModule } from './pages/dashboard/dashboard.module';
import { DepartmentDetailModule } from './pages/department-details/department-detail.module';
import { DepartmentRegisterModule } from './pages/department-register/department-register.module';
import { DeviceDetailModule } from './pages/device-details/device-detail.module';
import { DeviceRegisterModule } from './pages/device-register/device-register.module';
import { EditAllowedIpModule } from './pages/edit-allowed-ip/edit-allowed-ip.module';
import { EditMailboxPinModule } from './pages/edit-mailbox-pin/edit-mailbox-pin.module';
import { EditPresenceContactModule } from './pages/edit-presence-contact/edit-presence-contact.module';
import { EditPresenceCredentialsModule } from './pages/edit-presence-credentials/edit-presence-credentials.module';
import { ExtensionDetailModule } from './pages/extension-details/extension-detail.module';
import { FaxEmailsModule } from './pages/extension-details/views/extension-fax-sections/fax-emails-tab/fax-emails.module';
import { ExtensionGroupDetailModule } from './pages/extension-group-details/extension-group-detail.module';
import { ExtensionGroupRegisterModule } from './pages/extension-groups-register/extensions-groups-register.module';
import { ExtensionRegisterModule } from './pages/extension-register/extensions-register.module';
import { IncomingFaxRegisterModule } from './pages/fax-register/incoming-fax-register/incoming-fax-register.module';
import { OutgoingFaxRegisterModule } from './pages/fax-register/outgoing-fax-register/outgoing-fax-register.module';
import { LocationDetailModule } from './pages/location-details/location-detail.module';
import { LocationsRegisterModule } from './pages/locations-register-component/locations-register.module';
import { LoginModule } from './pages/login/login.module';
import { MailboxDetailStandaloneModule } from './pages/mailbox-details/mailbox-detail.module';
import { MailboxRegisterModule } from './pages/mailbox-register/mailbox-register.module';
import { PhoneNumberDetailModule } from './pages/phone-number-details/phone-number-detail.module';
import { PhoneNumberRegisterModule } from './pages/phone-number-register/phone-number-register.module';
import { PresenceContactRegisterModule } from './pages/presence-contact-register/presence-contact-register.module';
import { PromptDetailModule } from './pages/prompt-details/prompt-detail.module';
import { PromptRegisterModule } from './pages/prompt-register/prompt-register.module';
import { QueueHistoryRegisterModule } from './pages/queue-history-register/queue-history-register.module';
import { ReportPermissionsListViewModule } from './pages/report-permissions-list/report-permissions-list.module';
import { RoleDetailModule } from './pages/role-details/role-detail.module';
import { RoleRegisterModule } from './pages/role-register/role-register.module';
import { ScheduleDetailModule } from './pages/schedule-details/schedule-detail.module';
import { ScheduleRegisterModule } from './pages/schedule-register/schedule-register.module';
import { SiteLayoutModule } from './pages/site-layout/site-layout.module';
import { SystemLogRegisterModule } from './pages/system-log-register/system-log-register.module';
import { UserDetailModule } from './pages/user-details/user-detail.module';
import { UserPasswordResetModule } from './pages/user-password-reset/user-password-reset.module';
import { UserPreferenceDetailModule } from './pages/user-preferences/user-preferences.module';
import { UserRegisterModule } from './pages/user-register/user-register.module';
import { httpInterceptorProviders } from './providers/http-interceptor-providers';
import { LcsProviders } from './providers/lcs-providers';
import { materialProviders } from './providers/material-providers';
import { ExtendedRouterStateSerializer } from './router/extended-router-state-serializer';
import { NdtHubConnectionEffects } from './signalr/ndt-hub-connection.effects';
import { SelectAccountEffects } from './store/select-account/select-account.effects';
import { UserPreferencesModule } from './store/user-preferences/user-preferences.module';
import { MAT_TOOLTIP_DEFAULT_OPTIONS, MatTooltipDefaultOptions } from '@angular/material/tooltip';
const ngrxConfig: RootStoreConfig<AppState> = {
   runtimeChecks: {
      // TODO: enable these once we're more stable
      strictActionImmutability: true,
      strictStateImmutability: true,
      // strictActionSerializability: true,
      // strictStateSerializability: true,
   },
};

export function initConfig(appConfig: AppConfigService) {
   return () => {
      return appConfig.loadConfig();
   };
}

const tooltipDefaults: MatTooltipDefaultOptions = {
   showDelay: 0,
   hideDelay: 0,
   touchendHideDelay: 1500,
   disableTooltipInteractivity: true
}

@NgModule({
   bootstrap: [AppComponent],
   declarations: [AppComponent],
   imports: [
      AppRoutingModule,
      AvatarModule,
      AuthenticationModule,
      BrowserAnimationsModule,
      BrowserModule,
      ConfirmationDialogModule,
      DashboardModule,
      LoginModule,
      HttpClientModule,
      MatSnackBarModule,
      NdtLogoModule,
      SiteLayoutModule,
      UserRegisterModule,
      AccountRegisterModule,
      AccountDetailModule,
      CallCostDetailModule,
      GlobalSearchModule,
      ActiveCallsRegisterModule,
      CallLogsRegisterModule,
      QueueHistoryRegisterModule,
      OutgoingFaxRegisterModule,
      IncomingFaxRegisterModule,
      AddDeviceModule,
      ExtensionRegisterModule,
      MailboxRegisterModule,
      MailboxDetailStandaloneModule,
      SystemLogRegisterModule,
      DeviceRegisterModule,
      AddExtensionGroupModule,
      ExtensionDetailModule,
      ExtensionGroupRegisterModule,
      PhoneNumberRegisterModule,
      PhoneNumberDetailModule,
      BlockedPhoneNumberRegisterModule,
      AllowedIpRegisterModule,
      PresenceContactRegisterModule,
      BannedProvisioningIpRegisterModule,
      RoleRegisterModule,
      RoleDetailModule,
      ReportPermissionsListViewModule,
      PromptDetailModule,
      ErrorMessageModule,
      DeviceDetailModule,
      LocationsRegisterModule,
      ScheduleRegisterModule,
      DepartmentRegisterModule,
      PromptRegisterModule,
      AddExtensionModule,
      AddPhoneModule,
      UserPasswordResetModule,
      AddBlockedPhoneNumberModule,
      AddHardphoneModule,
      FaxEmailsModule,
      AddUserModule,
      AddPromptModule,
      AddProviderCallCostModule,
      AddScheduleModule,
      AddDepartmentModule,
      AddPhoneNumberModule,
      AddAccountModule,
      AddLocationModule,
      AddRoleModule,
      AccessDeniedModule,
      EditMailboxPinModule,
      EditPresenceContactModule,
      EditAllowedIpModule,
      EditPresenceCredentialsModule,
      AccountVerificationModule,
      AutoAttendantKeyListModule,
      UserDetailModule,
      UserPreferencesModule,
      UserPreferenceDetailModule,
      UserRegisterModule,
      ScheduleDetailModule,
      DepartmentDetailModule,
      ExtensionGroupDetailModule,
      CallCostRegisterModule,
      LocationDetailModule,
      PrivilegeListModule,
      NgxChartsModule,
      CallParkingLotRegisterModule,
      StoreModule.forRoot(reducers, ngrxConfig),
      EffectsModule.forRoot([
         SelectAccountEffects,
         AuthenticationEffects,
         AppEffects,
         AppStartEffects,
         AccountOptionEffects,
         DeviceOptionEffects,
         DepartmentOptionEffects,
         ExtensionTypeOptionEffects,
         DeviceTypeOptionEffects,
         PhoneOptionEffects,
         PhoneNumberOptionEffects,
         ExtensionOptionEffects,
         ExtensionGroupOptionEffects,
         PromptOptionEffects,
         LocationOptionEffects,
         ReportEffects,
         RoleOptionEffects,
         ScheduleOptionEffects,
         ServerOptionEffects,
         UserOptionEffects,
         NdtHubConnectionEffects,
         MatDialogModule,
         BulkConfigureExtensionsDialogModule,
      ]),
      StoreRouterConnectingModule.forRoot({
         serializer: ExtendedRouterStateSerializer,
      }),
      !environment.production ? StoreDevtoolsModule.instrument() : [],
      HttpClientXsrfModule.withOptions({ cookieName: 'ndt-xsrf-cookie', headerName: 'ndt-xsrf-header' }),
   ],
   providers: [
      AppConfigService,
      provideAppInitializer(() => {
         const initializerFn = initConfig(inject(AppConfigService));
         return initializerFn();
      }),
      ...LcsProviders,
      ...materialProviders,
      ...httpInterceptorProviders,
      { provide: EnvironmentService, useClass: NdtEnvironmentService },
      {
         deps: [LcsDateAdapter],
         provide: LCS_DATE_RANGE_PICKER_OPTIONS,
         useFactory: dateRangePickerOptionsForCallHistory,
      },
      {
         provide: MAT_TOOLTIP_DEFAULT_OPTIONS,
         useValue: tooltipDefaults
      },
   ],
})
export class AppModule {}
